import React, { useState, useEffect } from "react";
import moment from "moment";
import Table from "../../components/Table";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import Toast from "../../components/Toast";
import AssayForm from "./AssayForm";
import {
  getData,
  postData,
  GET_ASSAY_URL,
  ADD_ASSAY,
} from "../../utils/api";

const AssaySubweight = (props) => {
  const { isCreate, setIsCreate } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletePopupShown, setIsDeletePopupShown] = useState(false);
  const [isPopupShown, setIsPopupShown] = useState(false);
  const [itemForDelete, setItemForDelete] = useState(null);
  const [params, setParams] = useState({
    limit: 25,
    offset: 0,
    areMoreItemsAvailable: false,
  });
  const [toastParams, setToastParams] = useState({
    message: "",
    type: "",
    id: "",
  });
  const [assayData, setAssayData] = useState({
    ptAssay: "",
    pdAssay: "",
    rhAssay: "",
    subweight: "",
  });
  const [historyItems, setHistoryItems] = useState([]);

  useEffect(() => {
    setIsPopupShown(isCreate);
  }, [isCreate]);

  const resetData = () => {
    setAssayData({
      ptAssay: "",
      pdAssay: "",
      rhAssay: "",
      subweight: "",
    });
  };

  const fetchMore = () => {
    setParams({
      ...params,
      offset: params.offset + params.limit,
    });
  };

  const fetchLess = () => {
    setParams({
      ...params,
      offset: params.offset - params.limit,
    });
  };

  const getAssayHistory = async () => {
    try {
      setIsLoading(true);
      const data = await getData(GET_ASSAY_URL, {
        count: params.limit,
        offset: params.offset,
      });
      setIsLoading(false);
      // Map weight to subweight for display consistency
      const mappedItems = (data.list || []).map((item) => ({
        ...item,
        subweight: item.weight,
      }));
      setHistoryItems(mappedItems);
      setParams({
        ...params,
        areMoreItemsAvailable: data.areMoreItemsAvailable,
      });
    } catch (e) {
      console.log("ERROR", e);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAssayHistory();
  }, [params.offset, params.limit]);

  const setDelete = (item) => {
    setIsDeletePopupShown(true);
    setItemForDelete(item);
  };

  const setEdit = (item) => {
    setIsCreate(true);
    // Map weight to subweight if needed for consistency
    setAssayData({
      ...item,
      subweight: item.subweight || item.weight || "",
    });
  };

  // TODO: Implement API call when provided
  const deleteItem = async () => {
    setIsLoading(true);
    try {
      // const response = await deleteData(DELETE_ASSAY, {
      //   assayId: itemForDelete.modelId,
      // });
      setIsDeletePopupShown(false);
      setIsLoading(false);
      getAssayHistory();
      setToastParams({
        type: "SUCCESS",
        message: "Assay deleted successfully",
        id: new Date().getTime(),
      });
    } catch (e) {
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : e.response?.data?.error || "Failed to delete assay",
        id: new Date().getTime(),
      });
      setIsDeletePopupShown(false);
      setIsLoading(false);
    }
  };

  const postAssay = async () => {
    try {
      if (
        !assayData.ptAssay ||
        !assayData.pdAssay ||
        !assayData.rhAssay ||
        !assayData.subweight
      ) {
        setToastParams({
          type: "ERROR",
          message: "Please fill all fields",
          id: new Date().getTime(),
        });
        return;
      }
      
      setIsLoading(true);
      
      // Map subweight to weight and convert to double
      const payload = {
        ptAssay: parseFloat(assayData.ptAssay),
        pdAssay: parseFloat(assayData.pdAssay),
        rhAssay: parseFloat(assayData.rhAssay),
        weight: parseFloat(assayData.subweight),
      };

      const data = await postData(ADD_ASSAY, payload);

      resetData();
      setIsLoading(false);
      setIsCreate(false);
      setToastParams({
        type: "SUCCESS",
        message:
          data &&
          data.response &&
          data.response.data &&
          data.response.data.message
            ? data.response.data.message
            : "Assay added successfully",
        id: new Date().getTime(),
      });
      getAssayHistory();
    } catch (e) {
      console.log("ERROR", e);
      setIsLoading(false);
      setToastParams({
        type: "ERROR",
        message:
          e && e.response && e.response.data && e.response.data.error
            ? e.response.data.error
            : "Please enter valid input",
        id: new Date().getTime(),
      });
    }
  };

  return (
    <>
      <Table
        customClassName="pr-16"
        scrollTable={true}
        columns={["PT Assay", "PD Assay", "RH Assay", "Subweight", "Created"]}
        items={(historyItems || []).map((history) => {
          const createdDate = moment(history.created).format(
            "MM/DD/YYYY hh:mm A"
          );
          return {
            ...history,
            created: `${createdDate}`,
          };
        })}
        keys={["ptAssay", "pdAssay", "rhAssay", "subweight", "created"]}
        offset={params.offset}
        areMoreItemsAvailable={params.areMoreItemsAvailable}
        fetchMore={fetchMore}
        fetchLess={fetchLess}
        isLoading={isLoading}
        counterDropdown={[25, 50, 100]}
        className="min-wt-1000"
        hideCheckBox={true}
        onCountChange={(value) => {
          setParams({
            ...params,
            limit: value,
          });
        }}
        limit={params.limit}
        actions={["EDIT", "DELETE"]}
        onEdit={setEdit}
        onDelete={setDelete}
      />

      {isPopupShown && (
        <AssayForm
          isOpen={isPopupShown}
          onClose={() => {
            resetData();
            setIsCreate(false);
          }}
          assayData={assayData}
          onChange={setAssayData}
          onSubmit={postAssay}
          isEdit={!!assayData?.modelId}
        />
      )}

      {isDeletePopupShown && (
        <DeleteConfirmation
          type="Assay"
          name={itemForDelete?.ptAssay || "this assay"}
          onClose={setIsDeletePopupShown.bind(null, false)}
          onConfirm={deleteItem}
        />
      )}
      <Toast
        type={toastParams.type}
        message={toastParams.message}
        id={toastParams.id}
      />
    </>
  );
};

export default AssaySubweight;
